/**
 * Premium PDF report hook (ISSUE-052 / T5.3 / web client).
 *
 * Drives the two-step flow behind the reports_export gate: POST /me/reports/pdf
 * enqueues a render, then GET /me/reports/pdf/:id is polled until the signed
 * download link is ready. Mirrors the GDPR-export polling pattern.
 */
import type { components } from '@nutians/api-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api.js';

type ReportJob = components['schemas']['ReportJob'];
export type ReportPeriod = 'week' | 'month';

export function useReportDownload() {
  const [jobId, setJobId] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: async (period: ReportPeriod): Promise<ReportJob> => {
      const { data, error } = await api.POST('/me/reports/pdf', { body: { period } });
      if (error || !data) throw new Error('Failed to start report');
      return data;
    },
    onSuccess: (job) => setJobId(job.id),
  });

  const status = useQuery({
    queryKey: ['report-job', jobId],
    enabled: jobId !== null,
    queryFn: async (): Promise<ReportJob> => {
      const { data, error } = await api.GET('/me/reports/pdf/{id}', {
        params: { path: { id: jobId as string } },
      });
      if (error || !data) throw new Error('Failed to load report status');
      return data;
    },
    // Poll every 2s until the render settles (ready | failed | expired).
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'ready' || s === 'failed' || s === 'expired' ? false : 2000;
    },
    retry: false,
  });

  const job = status.data;
  const isWorking =
    start.isPending ||
    (jobId !== null && (!job || job.status === 'pending' || job.status === 'processing'));

  return {
    start: (period: ReportPeriod) => start.mutate(period),
    reset: () => setJobId(null),
    isWorking,
    downloadUrl: job?.status === 'ready' ? (job.downloadUrl ?? null) : null,
    failed: job?.status === 'failed' || job?.status === 'expired' || start.isError,
    period: job?.period ?? null,
  };
}
